import { useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import { PaginationControls } from "metabase/common/components/PaginationControls";
import { usePagination } from "metabase/common/hooks/use-pagination";
import { CacheConfigApi } from "metabase/services";
import { Box, Flex, Loader, Select, Stack, Text, TextInput } from "metabase/ui";
import type { CacheConfigChange, PaginatedList } from "metabase-types/api";

import Styles from "./StrategyEditorForQuestionsAndDashboards.module.css";
import { formatDateTime } from "./utils";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE = 300;

const actionLabel = (action: CacheConfigChange["action"]) => {
  switch (action) {
    case "created":
      return t`Created`;
    case "updated":
      return t`Updated`;
    case "deleted":
      return t`Deleted`;
  }
};

const modelLabel = (model: CacheConfigChange["model"]) =>
  model === "dashboard" ? t`Dashboard` : t`Question`;

const actorLabel = (change: CacheConfigChange) => {
  const name = [change.user_first_name, change.user_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || change.user_email || t`Unknown`;
};

export const CacheChangeLog = () => {
  const { page, setPage, handleNextPage, handlePreviousPage } = usePagination();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [model, setModel] = useState<string | null>(null);
  const [result, setResult] = useState<PaginatedList<CacheConfigChange> | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(timer);
  }, [searchInput, setPage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    CacheConfigApi.changeLog({
      limit: PAGE_SIZE,
      offset: PAGE_SIZE * page,
      query: search || undefined,
      model: model || undefined,
    })
      .then((data: PaginatedList<CacheConfigChange>) => {
        if (!cancelled) {
          setResult(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({ data: [], total: 0 });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [page, search, model]);

  const rows = result?.data ?? [];
  const total = result?.total ?? 0;

  const modelOptions = useMemo(
    () => [
      { value: "", label: t`All types` },
      { value: "dashboard", label: t`Dashboards` },
      { value: "question", label: t`Questions` },
    ],
    [],
  );

  return (
    <Stack gap="md">
      <Flex gap="md" wrap="wrap" align="end">
        <TextInput
          label={t`Search by name`}
          placeholder={t`Dashboard or question name`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          w="20rem"
        />
        <Select
          label={t`Type`}
          data={modelOptions}
          value={model ?? ""}
          onChange={(value) => {
            setPage(0);
            setModel(value || null);
          }}
          w="10rem"
        />
      </Flex>

      {loading ? (
        <Flex justify="center" py="lg">
          <Loader size="sm" />
        </Flex>
      ) : (
        <Box className={Styles.TableScrollContainer}>
          <table className={Styles.HistoryTable}>
            <thead>
              <tr>
                <th>{t`Name`}</th>
                <th>{t`Type`}</th>
                <th>{t`Action`}</th>
                <th>{t`Policy`}</th>
                <th>{t`Changed by`}</th>
                <th>{t`When`}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((change) => (
                <tr key={change.id}>
                  <td>{change.item_name ?? `#${change.model_id}`}</td>
                  <td className={Styles.CompactCell}>
                    {modelLabel(change.model)}
                  </td>
                  <td className={Styles.CompactCell}>
                    {actionLabel(change.action)}
                  </td>
                  <td className={Styles.CompactCell}>
                    {change.strategy ?? "-"}
                  </td>
                  <td className={Styles.CompactCell}>{actorLabel(change)}</td>
                  <td className={Styles.CompactCell}>
                    {formatDateTime(change.created_at)}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <Text c="text-light" fw="bold" ta="center" py="md">
                      {t`No cache configuration changes yet.`}
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      )}

      <PaginationControls
        page={page}
        pageSize={PAGE_SIZE}
        itemsLength={rows.length}
        total={total}
        showTotal
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
      />
    </Stack>
  );
};
